import { Button } from "@/components/ui/button";
import { centerElementInHorizontalScroller } from "@/lib/explicitScrollNavigation";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  className?: string;
  /** Applied to the tab scroller only (e.g. reserve space under a fixed logo). */
  scrollAreaClassName?: string;
  activeTabId?: string;
  /** Increment only on explicit user tab selection to center the tab horizontally. */
  centerActiveTabNonce?: number;
  "data-testid"?: string;
};

export default function ScrollableTabRow({
  children,
  className,
  scrollAreaClassName,
  activeTabId,
  centerActiveTabNonce = 0,
  "data-testid": testId,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollHints = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollHints();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollHints, { passive: true });
    const observer = new ResizeObserver(updateScrollHints);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollHints);
      observer.disconnect();
    };
  }, [updateScrollHints]);

  useEffect(() => {
    if (!activeTabId || centerActiveTabNonce <= 0) return;
    const el = scrollRef.current;
    if (!el) return;
    const activeButton = el.querySelector<HTMLElement>(`[data-tab-id="${CSS.escape(activeTabId)}"]`);
    if (!activeButton) return;
    centerElementInHorizontalScroller(el, activeButton);
  }, [activeTabId, centerActiveTabNonce]);

  const scrollByPage = (direction: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(160, el.clientWidth * 0.6), behavior: "smooth" });
  };

  const rightScrollButton = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        "h-8 w-8 rounded-full border border-[#1A2744]/10 bg-background text-[#1A2744]/70 shadow-sm hover:bg-muted",
        !canScrollRight && "pointer-events-none opacity-40 sm:hidden",
      )}
      aria-label="Scroll tabs right"
      disabled={!canScrollRight}
      onClick={() => scrollByPage(1)}
    >
      <ChevronRight className="h-4 w-4" />
    </Button>
  );

  return (
    <div className={cn("flex w-full items-stretch", className)} data-testid={testId}>
      <div className="flex min-w-0 flex-1 items-center ps-[max(0.25rem,env(safe-area-inset-left))]">
        <div className="hidden shrink-0 sm:flex sm:w-9 sm:justify-center">
          {canScrollLeft ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full border border-[#1A2744]/10 bg-background text-[#1A2744]/70 shadow-sm hover:bg-muted"
              aria-label="Scroll tabs left"
              onClick={() => scrollByPage(-1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          ) : (
            <span className="h-8 w-8" aria-hidden />
          )}
        </div>

        <div
          ref={scrollRef}
          className={cn(
            "min-w-0 flex-1 overflow-x-auto scroll-smooth [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            scrollAreaClassName,
          )}
        >
          <div className="flex w-max gap-2 px-0.5 py-0.5">{children}</div>
        </div>
      </div>

      <div
        className={cn(
          "flex shrink-0 items-center justify-center self-stretch border-l border-[#1A2744]/10 bg-background/95",
          "w-10 pe-[max(0.25rem,env(safe-area-inset-right))] sm:w-9 sm:border-l-0 sm:bg-transparent",
        )}
        data-testid={testId ? `${testId}-right-control` : undefined}
      >
        {rightScrollButton}
        {!canScrollRight ? <span className="hidden h-8 w-8 sm:inline-block" aria-hidden /> : null}
      </div>
    </div>
  );
}
